import { Product } from '../entities/Product';
import { ProductRepository } from '../../repositories/ProductRepository';
import { SortingTypes } from '../../constants/SortingTypes';
import { observable } from 'mobx';
import { ProductGridSettings } from '../entities/ProductGridSettings';
import { InventoryStatusSortingStrategy } from './InventoryStatusSortingStrategy';

export class ProductSortingService {
    public productRepository: ProductRepository;
    @observable private productGridSettings: ProductGridSettings;
    private featuredProductId: string;

    constructor(productGridSettings: ProductGridSettings, productRepository: ProductRepository) {
        this.productGridSettings = productGridSettings;
        this.productRepository = productRepository;
    }

    // set sort criteria
    public sortProducts(productList: Product[], featuredProductId?: string) {
        const sortFunction = this.getProductsSortFunction();

        if (featuredProductId) {
            this.featuredProductId = featuredProductId;
        }

        if (sortFunction) {
            return productList.slice().sort(sortFunction).sort(this.byFeatured);
        }

        return this.sortByInventoryStatus(productList).slice().sort(this.byFeatured);
    }

    private getProductsSortFunction = () => {
        switch (this.productGridSettings.sortValue) {
            case SortingTypes.SORT_TOP_RATED:
                return this.byReview;
            case SortingTypes.SORT_PRICE_LOW_TO_HIGH:
            case SortingTypes.SORT_PRICE_HIGH_TO_LOW:
                return this.byPrice;
            default:
                return null;
        }
    };

    private sortByInventoryStatus = (products: Product[]) => {
        if (!this.productGridSettings.enableSortByInventoryStatus) {
            return products;
        }

        const availableProducts = products.filter(this.isProductShoppable);
        const remainingProducts = products.filter(this.isProductUnavailable);

        if (this.productGridSettings.sortInventoryStatuses.length) {
            const inventoryStatusSorting = new InventoryStatusSortingStrategy(
                this.productGridSettings.sortInventoryStatuses,
                this.productGridSettings.enableBackorder
            );
            remainingProducts.sort(inventoryStatusSorting.sortProductsByInventory);
        }

        return availableProducts.concat(remainingProducts);
    };

    private isProductShoppable = (product: Product) => {
        const isOutOfStockNoBackorder =
            product.isOutOfStock && !this.productGridSettings.enableBackorder;

        return !(isOutOfStockNoBackorder || product.isSoldOut);
    };

    private isProductUnavailable = (product: Product) => {
        return !this.isProductShoppable(product);
    };

    private byReview = (firstProduct: Product, nextProduct: Product) => {
        const ratingA = firstProduct.reviews?.averageRating || 0;
        const ratingB = nextProduct.reviews?.averageRating || 0;

        if (ratingA > ratingB) {
            return this.productGridSettings.sortValue === SortingTypes.SORT_TOP_RATED ? -1 : 1;
        }

        return 0;
    };

    private byPrice = (a: Product, b: Product) => {
        const priceA = a.defaultPrice;
        const priceB = b.defaultPrice;

        if (priceA > priceB) {
            return this.productGridSettings.sortValue === SortingTypes.SORT_PRICE_HIGH_TO_LOW
                ? -1
                : 1;
        }
        if (priceA < priceB) {
            return this.productGridSettings.sortValue === SortingTypes.SORT_PRICE_LOW_TO_HIGH
                ? -1
                : 1;
        }

        return 0;
    };

    private byFeatured = (a: Product, b: Product) => {
        if (!this.featuredProductId) {
            return 0;
        }

        if (a.productId === this.featuredProductId) {
            return -1;
        }
        if (b.productId === this.featuredProductId) {
            return 1;
        }

        return 0;
    };
}
